/**
 * Compact VAT-rate control: shows the current rate (e.g. "25,5%") and opens a
 * menu on click so staff can change it. Used on work-order service part rows
 * where the price already includes VAT — this is not a free-text input.
 */

import { useId, useMemo, useState } from 'react';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import type { VatRate } from '@/types';
import { formatVatPercent } from '@/utils/vat';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';

export interface VatRatePickerProps {
  value: number;
  rates: VatRate[];
  onChange: (percent: number) => void;
  /** Visible tooltip + accessible name, e.g. "Sis. ALV". */
  label: string;
}

export function VatRatePicker({ value, rates, onChange, label }: VatRatePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();
  const { order: orderVatRates, record: recordVat } = useDropdownMru(DROPDOWN_MRU_SELECTORS.vat);
  const orderedRates = useMemo(
    () => orderVatRates(rates, (rate) => rate.id),
    [rates, orderVatRates],
  );

  return (
    <>
      <Tooltip title={label}>
        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.stopPropagation();
          }}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            flexShrink: 0,
            minWidth: 0,
            px: 0.75,
            color: 'text.secondary',
            fontWeight: 500,
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {formatVatPercent(value)}
        </Button>
      </Tooltip>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.stopPropagation();
        }}
        slotProps={{ list: { 'aria-label': label } }}
      >
        {orderedRates.map((rate) => {
          const selected = rate.percentage === value;
          return (
            <MenuItem
              key={rate.id}
              selected={selected}
              onClick={() => {
                recordVat(rate.id);
                onChange(rate.percentage);
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>{selected ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>{rate.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
